import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight, Cpu, BookOpen, Brain, Lightbulb, Target } from 'lucide-react';
import { Section, SectionTitle, AnimatedCard } from '@/components/Section';
import { Button } from '@/components/ui/button';
import { AboutHero } from '@/components/AboutHero';
import { SingularVisionSection } from '@/components/SingularVisionSection';

export default function AboutPage() {
  return (
    <main className="pt-20">
      <AboutHero />

      {/* Mission & Philosophy */}
      <Section dark>
        <SectionTitle subtitle="Mission">Philosophy & Vision</SectionTitle>
        <div className="max-w-4xl mx-auto text-center">
          <motion.blockquote
            className="text-2xl md:text-3xl text-slate-200 italic leading-relaxed mb-8"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            "Technology should expand human capability, not diminish it. The best systems are practical, the best innovations are useful, and the best builders never stop being curious."
          </motion.blockquote>
          
          <motion.p
            className="text-lg text-muted-foreground leading-relaxed"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
          >
            Robert's work is guided by systems thinking and a curiosity-driven approach to innovation. He believes that artificial intelligence, properly designed and deployed, can augment human creativity rather than replace it. This philosophy — combined with a builder's hands-on instinct — informs every venture, every book, and every consulting engagement.
          </motion.p>
        </div>
      </Section>

      {/* Systems Experience */}
      <Section id="systems-experience">
        <div className="max-w-4xl mx-auto">
          <motion.div
            className="mb-8"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <span className="text-sm text-primary font-medium uppercase tracking-widest mb-3 block">Foundation</span>
            <h2 className="text-3xl md:text-4xl font-bold text-white" data-testid="systems-experience-heading">
              Systems Experience
            </h2>
          </motion.div>

          <div className="space-y-5 mb-10">
            {[
              "For over three decades, I've designed and built operational systems across industries including banking, aerospace, defense, nonprofit organizations, and small business environments.",
              "My work has focused on developing tools that track, analyze, and support real-world decision-making\u2014ranging from financial systems and risk management platforms to enterprise reporting and workflow automation solutions.",
              "These systems were built to solve practical problems: improving visibility, increasing efficiency, and enabling better decisions at scale.",
            ].map((text, i) => (
              <motion.p
                key={text.slice(0, 40)}
                className="text-lg text-slate-300 leading-relaxed"
                initial={{ opacity: 0, y: 15 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.05 }}
              >
                {text}
              </motion.p>
            ))}
          </div>

          <motion.blockquote
            className="text-xl md:text-2xl text-white font-semibold leading-snug border-l-4 border-primary pl-6 mb-10"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            data-testid="systems-experience-quote"
          >
            Long before artificial intelligence became mainstream, I was building the structured systems it now depends on.
          </motion.blockquote>

          <motion.p
            className="text-lg text-slate-300 leading-relaxed mb-10"
            initial={{ opacity: 0, y: 15 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            Today, that same approach extends into AI—where the focus is not just on capability, but on how intelligent systems integrate into real operational environments.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h3 className="text-sm font-semibold text-primary uppercase tracking-wider mb-4">Selected Systems & Platforms</h3>
            <div className="grid sm:grid-cols-2 gap-3">
              {[
                'Risk Management System (Defense Environment)',
                'Loan Pricing and Financial Modeling Systems (Banking)',
                'Satellite Configuration and Tracking Systems (DoD)',
                'Enterprise Reporting and Workflow Automation Platforms',
                'Inventory, Order Management, and Operational Tracking Systems',
              ].map((system, i) => (
                <div key={system} className="flex items-start gap-3 p-3 rounded-lg bg-slate-800/30 border border-border/10">
                  <div className="w-1.5 h-1.5 rounded-full bg-primary mt-2.5 flex-shrink-0" />
                  <span className="text-sm text-slate-300">{system}</span>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </Section>

      <SingularVisionSection />

      {/* Early Life Section */}
      <Section>
        <SectionTitle subtitle="Origins">Early Life & Cultural Roots</SectionTitle>
        <div className="max-w-4xl">
          <motion.p
            className="text-lg text-slate-300 leading-relaxed mb-6"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            Born and raised in Louisiana, Robert Brunet's early life was shaped by the rich cultural heritage of the South—a blend of resilience, creativity, and community that would later inform his approach to technology and entrepreneurship. The values instilled during these formative years—hard work, resourcefulness, and the importance of storytelling—became the foundation for everything that followed.
          </motion.p>
          <motion.p
            className="text-lg text-slate-300 leading-relaxed"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
          >
            Growing up in an environment where making the most of what you have was not just a philosophy but a necessity, Brunet developed an entrepreneurial mindset long before entering the business world. These Louisiana roots continue to influence his work today, grounding high-tech innovation in deeply human values.
          </motion.p>
        </div>
      </Section>

      {/* Technology Career Section */}
      <Section dark>
        <SectionTitle subtitle="Career">Technology Leadership</SectionTitle>
        <div className="grid lg:grid-cols-2 gap-12 items-start">
          <div>
            <motion.p
              className="text-lg text-muted-foreground leading-relaxed mb-6"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              With over four decades in the technology industry, Robert has worked across the full spectrum of enterprise computing—from infrastructure architecture to systems integration, from data management to emerging AI. He sees how tools connect and understands what it takes to make technology work in real organizations.
            </motion.p>
            <motion.p
              className="text-lg text-muted-foreground leading-relaxed"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
            >
              His perspective spans mainframe systems through cloud computing to today's AI revolution—giving him rare insight into how technology evolves and how businesses can adapt without losing what already works.
            </motion.p>
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            {[
              { icon: Cpu, label: 'Systems Architecture' },
              { icon: Brain, label: 'AI & Machine Learning' },
              { icon: Target, label: 'Strategic Planning' },
              { icon: Lightbulb, label: 'Innovation Leadership' }
            ].map((item, index) => (
              <AnimatedCard key={item.label} delay={index * 0.1}>
                <item.icon className="w-8 h-8 text-primary mb-3" />
                <span className="text-white font-medium">{item.label}</span>
              </AnimatedCard>
            ))}
          </div>
        </div>
      </Section>

      {/* Entrepreneurial Journey */}
      <Section>
        <SectionTitle subtitle="Entrepreneurship">Building Ventures</SectionTitle>
        <motion.p
          className="text-lg text-slate-300 leading-relaxed mb-8 max-w-4xl"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          Throughout his career, Brunet has founded and led multiple ventures, each addressing a unique challenge at the intersection of technology and human need. From LifeCapsule AI's digital legacy preservation to NewAudia's audio innovation, from Digital Ink AI's publishing technology to the entrepreneurial education of AI Means Business—each venture reflects a commitment to building meaningful solutions.
        </motion.p>
        
        <Button asChild className="bg-primary text-primary-foreground hover:bg-primary/90" data-testid="explore-ventures-btn">
          <Link to="/ventures">Explore All Ventures <ArrowRight className="ml-2 h-4 w-4" /></Link>
        </Button>
      </Section>

      {/* Publishing Work */}
      <Section dark>
        <SectionTitle subtitle="Author">Writing & Publishing</SectionTitle>
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <p className="text-lg text-muted-foreground leading-relaxed mb-6">
              As a published author, Brunet has written extensively on topics ranging from artificial intelligence and technology strategy to entrepreneurship and cultural commentary. His writing reflects the same practical, systems-oriented thinking that characterizes his technology work.
            </p>
            <p className="text-lg text-muted-foreground leading-relaxed mb-6">
              Through Use What You Got Publishing, he has also helped other authors bring their work to market, sharing the knowledge and tools needed to succeed in modern publishing.
            </p>
            <Button asChild variant="outline" className="border-primary text-primary hover:bg-primary/10" data-testid="view-books-btn">
              <Link to="/books">View Published Works <ArrowRight className="ml-2 h-4 w-4" /></Link>
            </Button>
          </motion.div>
          
          <motion.div
            className="relative"
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
          >
            <img
              src="https://customer-assets.emergentagent.com/job_ai-architect-55/artifacts/n3dbm1xh_UWYGP_Logo_Small.jpg"
              alt="Use What You Got Publishing"
              className="rounded-xl w-full"
            />
          </motion.div>
        </div>
      </Section>

      {/* Also Known As Chef BB */}
      <Section>
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
          >
            <img
              src="https://customer-assets.emergentagent.com/job_ai-architect-55/artifacts/4juwwhm4_Chef_BB_Promo_Image.jpg"
              alt="Chef BB"
              className="rounded-xl w-full max-w-md mx-auto"
            />
          </motion.div>
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <span className="text-sm text-primary font-medium uppercase tracking-widest mb-4 block">Also Known As</span>
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">Chef BB</h2>
            <p className="text-lg text-slate-300 leading-relaxed mb-6">
              Beyond the boardroom and beyond the books, there's another side to Robert Brunet. As Chef BB, he brings the same passion for excellence and creativity to the culinary world—blending Louisiana heritage with modern techniques.
            </p>
            <p className="text-lg text-muted-foreground leading-relaxed">
              This culinary identity reflects the same philosophy that guides all his work: use what you have, honor your roots, and create something meaningful. Whether building technology systems or crafting a meal, the principles remain the same.
            </p>
          </motion.div>
        </div>
      </Section>

      {/* Dr. Synthia Speaks */}
      <Section dark>
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
          >
            <span className="text-sm text-primary font-medium uppercase tracking-widest mb-4 block">AI Guide</span>
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">Dr. Synthia Speaks</h2>
            <p className="text-lg text-slate-300 leading-relaxed mb-6">
              Meet Dr. Synthia—an AI avatar created to serve as an actual AI guide within Brunet's books and educational content. She represents the practical application of human-AI collaboration in education and thought leadership.
            </p>
            <p className="text-lg text-muted-foreground leading-relaxed">
              Dr. Synthia embodies the belief that AI can be a helpful companion in learning and discovery, providing insights and guidance while always supporting—never replacing—human creativity and decision-making.
            </p>
          </motion.div>
          
          <motion.div
            className="flex justify-center gap-6"
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
          >
            <img
              src="https://customer-assets.emergentagent.com/job_ai-architect-55/artifacts/4ty1h5oc_Dr_Synthia_Profile_Pic.jpg"
              alt="Dr. Synthia Speaks"
              className="rounded-xl w-48 h-48 object-cover"
            />
            <img
              src="https://customer-assets.emergentagent.com/job_ai-architect-55/artifacts/nslalvjk_DrSynthiaSpeaks_Logo_NBG.png"
              alt="Dr. Synthia Speaks Logo"
              className="rounded-xl w-48 h-48 object-contain bg-card p-4"
            />
          </motion.div>
        </div>
      </Section>

      {/* CTA */}
      <Section>
        <div className="text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">Ready to Connect?</h2>
          <p className="text-lg text-muted-foreground mb-8 max-w-2xl mx-auto">
            Whether you're exploring AI adoption for your organization, interested in speaking engagements, or looking for entrepreneurial collaboration, I welcome the conversation.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Button asChild size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90" data-testid="contact-robert-btn">
              <Link to="/contact">Work With Me</Link>
            </Button>
            <Button asChild variant="outline" size="lg" className="border-primary text-primary hover:bg-primary/10" data-testid="view-services-btn">
              <Link to="/schedule">View Services</Link>
            </Button>
          </div>
        </div>
      </Section>
    </main>
  );
}
